/**
 * resolvedExclusion — operator-resolved exclusion (#1289 C3).
 *
 * Extracted from FilterBar's component-body closures so the feed view
 * and the dashboard tiles agree on which groups the operator already
 * handled. Parameterized on an optional optimistic-overrides map: the
 * canvas page passes its in-flight mutation state, the feed passes
 * nothing (folded rows expose no resolution actions — the persisted
 * ``manually_resolved_at`` attribute is sufficient).
 *
 * Group-level semantics (see the FilterBar header comment for the
 * per-side accounting rationale):
 *   - orphan_settlement → every settlement event resolved
 *   - orphan_delivery   → every delivery event resolved
 *   - partial           → BOTH sides fully resolved (marking one side
 *     only strips that side's contribution from the totals — handled by
 *     FilterBar's local ``stripResolvedEvents``)
 *   - matched           → never resolved-excluded
 */
import type { MatchGroup, MatchGroupLine } from '../../../hooks/useMatchGroups';

/**
 * Optimistic override wins (in both directions); falls back to the
 * persisted ``manually_resolved_at`` attribute (non-empty string ⇒
 * resolved) so a marked tile stays excluded across page reloads.
 */
export function isEventResolved(
  ev: Record<string, unknown> | null | undefined,
  overrides?: ReadonlyMap<string, boolean>,
): boolean {
  if (!ev) return false;
  const entityId = String(ev.entity_id ?? '');
  if (entityId && overrides) {
    const override = overrides.get(entityId);
    if (override !== undefined) return override;
  }
  const raw = ev.manually_resolved_at;
  return typeof raw === 'string' && raw.length > 0;
}

const seEventsOf = (g: MatchGroup): Record<string, unknown>[] =>
  g.allSettlementEvents.length > 0
    ? g.allSettlementEvents
    : g.settlementEvent
      ? [g.settlementEvent as Record<string, unknown>]
      : [];

const deEventsOf = (g: MatchGroup): Record<string, unknown>[] =>
  g.allDeliveryEvents.length > 0
    ? g.allDeliveryEvents
    : g.deliveryEvent
      ? [g.deliveryEvent as Record<string, unknown>]
      : [];

/**
 * Coerce an event's ``total_amount`` to a finite number (mirrors
 * FilterBar's ``readTotalAmount``). Returns ``0`` when the field is
 * absent / non-numeric so a missing figure subtracts nothing from the
 * recomputed money block.
 */
const readEventTotal = (ev: Record<string, unknown> | null | undefined): number => {
  if (!ev) return 0;
  const raw = ev.total_amount;
  if (raw == null) return 0;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
};

/**
 * #1436 / #2154 — rederive each surviving line's pairing against the
 * lines that are still present, pruning ``matchedLineIds`` entries that
 * point at a REMOVED counterpart line (dangling cross-refs). The mechanical
 * status follows the same rules ``useMatchGroups.buildLineLookups``
 * applies at hydration: a line with at least one surviving counterpart
 * keeps its kind (``quantity_mismatch`` ambers stay ambered — the qty
 * deviation is intrinsic to the pair that survived); a line whose every
 * counterpart vanished drops to ``unmatched``. ``matchedLineId`` (the
 * convenience first-counterpart) is kept consistent with the pruned
 * ``matchedLineIds[0]``.
 *
 * Shared by both per-event exclusion callers, each of which supplies its
 * own ``removedLineIds`` (the ids of lines dropped because their event
 * left the group):
 *   - ``excludeSuppressedEvents`` (#1395) — lines whose event was
 *     suppressed; it also consumes the ``brokeAPair``/``partialPrune``
 *     signals to rederive the group status.
 *   - ``FilterBar.stripResolvedEvents`` (#2154) — lines whose event was
 *     operator-resolved on ONE side of a partial; it ignores the signals
 *     (a per-side-resolved partial deliberately keeps its ``status``).
 *
 * Prunes ONLY the counterpart ids in ``removedLineIds`` — the lines whose
 * event left the group. We must NOT prune by "absent from the surviving
 * rendered set": ``useMatchGroups.enrichGoodsWithPackagingKolli`` folds
 * packaging-carrier lines OUT of ``settlementLines`` / ``deliveryLines``
 * BEFORE the group is built, so a perfectly valid ``matchedLineIds`` entry
 * can already point at a folded-away (consumed) line even with zero
 * removal — survivor-set pruning would wrongly drop it and flip an intact
 * line to ``unmatched`` (codex #1436 P2).
 *
 * Returns the rederived line arrays plus signals the caller uses to
 * rederive the group status:
 *   - ``brokeAPair`` — some surviving line lost ≥1 counterpart to a
 *     removed event (so the group's line topology actually changed; if
 *     false, a never-line-paired group keeps its engine status);
 *   - ``partialPrune`` — a fan-out (``≥2`` counterparts) line lost SOME
 *     but kept ≥1. Its kind is preserved (a surviving counterpart still
 *     reconciles per-pair), but the aggregate fan-out (#1291/#1391) is no
 *     longer guaranteed to sum, so the caller must not promote the group
 *     to ``matched`` (codex #1436 P2 — we don't re-run the sum aggregate
 *     per the context manifest; we just refuse the optimistic Vollständig).
 */
export function rederiveLinePairs(
  seLines: MatchGroupLine[],
  deLines: MatchGroupLine[],
  removedLineIds: ReadonlySet<string>,
): {
  settlementLines: MatchGroupLine[];
  deliveryLines: MatchGroupLine[];
  brokeAPair: boolean;
  partialPrune: boolean;
} {
  let brokeAPair = false;
  let partialPrune = false;
  const rederive = (lines: MatchGroupLine[]): MatchGroupLine[] =>
    lines.map((l) => {
      // Defensive: a partially-hydrated / legacy line may lack the array.
      if (!l.matchedLineIds || l.matchedLineIds.length === 0) return l;
      const survivingCounterparts = l.matchedLineIds.filter((id) => !removedLineIds.has(id));
      if (survivingCounterparts.length === l.matchedLineIds.length) return l;
      brokeAPair = true;
      if (survivingCounterparts.length > 0) partialPrune = true;
      const matchStatus: MatchGroupLine['matchStatus'] =
        survivingCounterparts.length === 0 ? 'unmatched' : l.matchStatus;
      return {
        ...l,
        matchStatus,
        matchedLineIds: survivingCounterparts,
        matchedLineId: survivingCounterparts[0] ?? null,
      };
    });

  return {
    settlementLines: rederive(seLines),
    deliveryLines: rederive(deLines),
    brokeAPair,
    partialPrune,
  };
}

/**
 * #1436 — rederive the group ``status`` from the (already-rederived)
 * surviving lines, used only when ``rederiveLinePairs`` actually broke a
 * cross-side pair AND both event-sides still survive. Mirrors the
 * worker's classification (``gutschrift_match_group.py``) restricted to
 * the surviving lines:
 *   - at least one surviving cross-pair (a line with a non-empty
 *     ``matchedLineIds``) → ``matched`` (every line clean) or ``partial``
 *     (any orphaned/qty-mismatch line remains);
 *   - no surviving cross-pair at all → the reconciliation collapsed. This
 *     function only runs when BOTH event-sides still survive (the caller's
 *     whole-side-strip arms handle the genuinely one-sided cases), so the
 *     bucket depends on whether each side still carries any surviving
 *     LINES:
 *       - both sides still have lines → two-sided component, nothing
 *         reconciles → ``partial`` (Abweichung). It must NOT flip to an
 *         orphan bucket: ``computeDashboardTotals`` counts only one side's
 *         lines for ``orphan_*``, so the other surviving side's amount
 *         would silently drop from the totals (codex #1436 r2 P2).
 *       - only the delivery side still has lines (the GS side's surviving
 *         events carry no lines) → "Gutschrift fehlt" (``orphan_delivery``);
 *       - only the settlement side still has lines → "Lieferschein fehlt"
 *         (``orphan_settlement``).
 */
function rederiveGroupStatus(
  seLines: MatchGroupLine[],
  deLines: MatchGroupLine[],
  partialPrune: boolean,
): MatchGroup['status'] {
  const hasSurvivingPair =
    seLines.some((l) => (l.matchedLineIds?.length ?? 0) > 0) ||
    deLines.some((l) => (l.matchedLineIds?.length ?? 0) > 0);
  if (hasSurvivingPair) {
    const allLines = [...seLines, ...deLines];
    // A line whose counterparts were partially pruned (fan-out lost some)
    // can no longer be trusted as a clean match — the aggregate sum is no
    // longer guaranteed (we don't re-run it here), so never optimistically
    // promote the group to Vollständig. Demote to ``partial`` (Abweichung).
    const clean = !partialPrune && allLines.every((l) => l.matchStatus === 'matched');
    return clean ? 'matched' : 'partial';
  }
  // Reconciliation collapsed — no line keeps a counterpart. Bucket by which
  // side still carries lines; a side that lost all its lines AND has no
  // surviving line means that side's documents are effectively gone from
  // the reconciliation. Both sides still carrying (now-unpaired) lines is a
  // two-sided Abweichung, NOT an orphan (keeps both amounts in the totals).
  const seHasLines = seLines.length > 0;
  const deHasLines = deLines.length > 0;
  if (seHasLines && deHasLines) return 'partial';
  if (deHasLines) return 'orphan_delivery';
  if (seHasLines) return 'orphan_settlement';
  // Neither side carries lines (events present but line-less): the engine
  // never line-reconciled them — fall back to ``partial`` rather than
  // inventing a one-sided orphan that would drop a surviving amount.
  return 'partial';
}

/**
 * #1395 — suppression is the stronger sibling of resolution. Where a
 * resolved event stays on the canvas (greyed, excluded from the
 * "needs attention" counts), a SUPPRESSED event — a QK /
 * Papierbestellung or any non-delivery doc that distorts the
 * reconciliation — is removed from the user view AND the totals
 * entirely. The persisted source of truth is the ``suppressed_at``
 * attribute on the event entity, written by ``twinService.editEntity``
 * exactly like ``manually_resolved_at``; the override semantics mirror
 * ``isEventResolved`` so the editor's kebab flips the tile instantly.
 */
export function isEventSuppressed(
  ev: Record<string, unknown> | null | undefined,
  overrides?: ReadonlyMap<string, boolean>,
): boolean {
  if (!ev) return false;
  const entityId = String(ev.entity_id ?? '');
  if (entityId && overrides) {
    const override = overrides.get(entityId);
    if (override !== undefined) return override;
  }
  const raw = ev.suppressed_at;
  return typeof raw === 'string' && raw.length > 0;
}

/**
 * #1395 — drop suppressed events (and their lines) from a group,
 * reseating each side's primary onto the first surviving event. Returns
 * the group untouched when nothing is suppressed (reference-stable so
 * consumers can skip work), and ``null`` once a group has no surviving
 * events on EITHER side — a fully-suppressed group must vanish from the
 * canvas, the feed, and the dashboard totals alike. A group whose one
 * side is fully suppressed but the other survives stays (the survivor
 * still needs the operator's eyes); the emptied side is cleared so its
 * (now-suppressed) ``total_amount`` no longer flows into the counts.
 */
export function excludeSuppressedEvents(
  g: MatchGroup,
  overrides?: ReadonlyMap<string, boolean>,
): MatchGroup | null {
  const suppressedSeIds = new Set<string>();
  for (const ev of g.allSettlementEvents) {
    const id = String(ev.entity_id ?? '');
    if (id && isEventSuppressed(ev, overrides)) suppressedSeIds.add(id);
  }
  const suppressedDeIds = new Set<string>();
  for (const ev of g.allDeliveryEvents) {
    const id = String(ev.entity_id ?? '');
    if (id && isEventSuppressed(ev, overrides)) suppressedDeIds.add(id);
  }
  // Single-event groups can carry the event only on the primary
  // ``settlementEvent`` / ``deliveryEvent`` (``allSettlementEvents`` is
  // empty for legacy facts / fixtures), so consult the primaries too.
  const primarySe = g.settlementEvent as Record<string, unknown> | null;
  const primaryDe = g.deliveryEvent as Record<string, unknown> | null;
  if (primarySe && isEventSuppressed(primarySe, overrides)) {
    suppressedSeIds.add(String(primarySe.entity_id ?? ''));
  }
  if (primaryDe && isEventSuppressed(primaryDe, overrides)) {
    suppressedDeIds.add(String(primaryDe.entity_id ?? ''));
  }
  if (suppressedSeIds.size === 0 && suppressedDeIds.size === 0) return g;

  const allSe = g.allSettlementEvents.filter(
    (e) => !suppressedSeIds.has(String(e.entity_id ?? '')),
  );
  const allDe = g.allDeliveryEvents.filter(
    (e) => !suppressedDeIds.has(String(e.entity_id ?? '')),
  );
  const seLines = g.settlementLines.filter((l) => !suppressedSeIds.has(l.eventId));
  const deLines = g.deliveryLines.filter((l) => !suppressedDeIds.has(l.eventId));

  // #1436 — the precise set of line ids dropped because their EVENT was
  // suppressed. Pairing rederivation prunes counterpart refs against THIS
  // set (not "absent from the surviving rendered lines"), so a folded-away
  // packaging-carrier line — legitimately missing from the rendered set
  // pre-suppression — is never mistaken for a vanished counterpart.
  const suppressedLineIds = new Set<string>();
  for (const l of g.settlementLines) {
    if (suppressedSeIds.has(l.eventId)) suppressedLineIds.add(l.lineId);
  }
  for (const l of g.deliveryLines) {
    if (suppressedDeIds.has(l.eventId)) suppressedLineIds.add(l.lineId);
  }

  // Reseat / clear each primary. A side with no surviving ``allXEvents``
  // but an un-suppressed primary (single-event fixture) keeps it; a side
  // whose primary was suppressed reseats onto ``allXEvents[0]`` (``null``
  // when none survive).
  const primarySeId = String(primarySe?.entity_id ?? '');
  const primaryDeId = String(primaryDe?.entity_id ?? '');
  const settlementEvent =
    primarySeId && suppressedSeIds.has(primarySeId)
      ? ((allSe[0] as MatchGroup['settlementEvent']) ?? null)
      : g.settlementEvent;
  const deliveryEvent =
    primaryDeId && suppressedDeIds.has(primaryDeId)
      ? ((allDe[0] as MatchGroup['deliveryEvent']) ?? null)
      : g.deliveryEvent;

  // Group is gone when neither side has a surviving event.
  const seSurvives = allSe.length > 0 || (!!settlementEvent && !suppressedSeIds.has(primarySeId));
  const deSurvives = allDe.length > 0 || (!!deliveryEvent && !suppressedDeIds.has(primaryDeId));
  if (!seSurvives && !deSurvives) return null;

  // Codex P2 — recompute status once a whole side is stripped. Downstream
  // FilterBar counts and feed filters key off ``status``: a partial /
  // matched group that loses its entire settlement side is no longer an
  // Abweichung but a "Gutschrift fehlt" (orphan_delivery); losing the
  // entire delivery side makes it "Lieferschein fehlt" (orphan_settlement).
  // When BOTH sides still survive (a sibling on a multi-event side was
  // suppressed) the two-sided shape is unchanged, so keep ``g.status``.
  // Orphan groups never had the other side, so they keep their status too
  // (caught by the both-survive / one-survive arms naturally).
  let status = g.status;
  let settlementLines = seLines;
  let deliveryLines = deLines;
  if (seSurvives && !deSurvives) {
    status = 'orphan_settlement';
  } else if (!seSurvives && deSurvives) {
    status = 'orphan_delivery';
  }

  // #1436 — when BOTH sides still survive, the whole-side recompute above
  // leaves the shape two-sided, but a line that was paired ONLY to a now-
  // suppressed event on the other side must lose its dangling
  // ``matchedLineIds`` and reclassify (a credit/delivery silently went
  // missing). Rederive the surviving lines, and — only if a real cross-
  // pair actually broke — rederive the group status from them so the
  // dashboard tiles + canvas filter agree the group is now an Abweichung
  // or an orphan rather than a stale ``matched``/``partial``.
  if (seSurvives && deSurvives) {
    const rederived = rederiveLinePairs(seLines, deLines, suppressedLineIds);
    settlementLines = rederived.settlementLines;
    deliveryLines = rederived.deliveryLines;
    if (rederived.brokeAPair) {
      status = rederiveGroupStatus(settlementLines, deliveryLines, rederived.partialPrune);
    }
  } else {
    // One whole side was stripped (status set above). The surviving side's
    // lines were all paired across to the vanished side, so prune their
    // now-dangling counterparts and flip them to ``unmatched`` — keeping
    // the per-line state consistent with the orphan group status.
    const rederived = rederiveLinePairs(seLines, deLines, suppressedLineIds);
    settlementLines = rederived.settlementLines;
    deliveryLines = rederived.deliveryLines;
  }

  // Codex P2 — recompute money so a suppressed event's component no
  // longer flows into the visible amount (the feed renders
  // ``group.money?.settlementTotal`` / ``deliveryTotal`` directly).
  // Subtract the suppressed events' ``total_amount`` from the engine's
  // authoritative totals rather than re-summing survivors: LS (delivery)
  // events carry no ``total_amount``, so re-summing would zero a real
  // ``deliveryTotal``. ``null`` money (legacy facts) stays ``null``.
  let money = g.money;
  if (money) {
    let suppressedSeTotal = 0;
    for (const e of g.allSettlementEvents) {
      if (suppressedSeIds.has(String(e.entity_id ?? ''))) suppressedSeTotal += readEventTotal(e);
    }
    if (primarySe && suppressedSeIds.has(primarySeId) && g.allSettlementEvents.length === 0) {
      // Single-event fixture: the suppressed SE lives only on the primary.
      suppressedSeTotal += readEventTotal(primarySe);
    }
    let suppressedDeTotal = 0;
    for (const e of g.allDeliveryEvents) {
      if (suppressedDeIds.has(String(e.entity_id ?? ''))) suppressedDeTotal += readEventTotal(e);
    }
    if (primaryDe && suppressedDeIds.has(primaryDeId) && g.allDeliveryEvents.length === 0) {
      suppressedDeTotal += readEventTotal(primaryDe);
    }
    const settlementTotal = money.settlementTotal - suppressedSeTotal;
    const deliveryTotal = money.deliveryTotal - suppressedDeTotal;
    money = {
      ...money,
      settlementTotal,
      deliveryTotal,
      delta: settlementTotal - deliveryTotal,
    };
  }

  return {
    ...g,
    status,
    money,
    allSettlementEvents: allSe,
    allDeliveryEvents: allDe,
    settlementLines,
    deliveryLines,
    settlementEvent,
    deliveryEvent,
  };
}

/**
 * #1436 — does a RAW group belong on the canvas under the operator's
 * ``statusFilter``? The dashboard tiles (``FilterBar``) count the
 * suppression-ADJUSTED set (``dashboardGroups``), so the canvas must
 * filter on the same adjusted status or the tile count won't match the
 * rows revealed (suppress one side of a ``partial`` → it becomes
 * ``orphan_delivery``; clicking "Gutschrift fehlt" counted it but the raw
 * ``g.status`` filter hid it).
 *
 * The caller still passes the RAW (un-stripped) group to ``MatchGraph``
 * so the suppressed tile keeps rendering muted with its "Wieder
 * einblenden" affordance — this predicate only decides membership.
 *
 *   - no filter → always in (so a fully-suppressed group is reachable to
 *     revert without an active filter).
 *   - fully-suppressed group (``excludeSuppressedEvents`` → ``null``): it
 *     is absent from every FilterBar tile count (it's dropped from
 *     ``dashboardGroups``), so it must NOT appear under every filter — that
 *     would reveal an uncounted row and re-break the count↔rows invariant.
 *     Show it under its RAW status filter only (the one bucket where an
 *     operator scanning that status would look to un-suppress it).
 *   - otherwise: the adjusted status must equal the filter.
 */
export function matchesCanvasStatusFilter(
  g: MatchGroup,
  statusFilter: string,
  overrides?: ReadonlyMap<string, boolean>,
): boolean {
  if (!statusFilter) return true;
  const adjusted = excludeSuppressedEvents(g, overrides);
  if (adjusted === null) return g.status === statusFilter;
  return adjusted.status === statusFilter;
}

/**
 * #1395 — map a group list through ``excludeSuppressedEvents``, dropping
 * the groups that fully vanish. The hook's consumers (editor canvas,
 * feed, dashboard totals) all run their data through this so suppression
 * removes a doc from the numbers AND every view in one pass.
 */
export function applySuppression(
  groups: MatchGroup[],
  overrides?: ReadonlyMap<string, boolean>,
): MatchGroup[] {
  const out: MatchGroup[] = [];
  for (const g of groups) {
    const kept = excludeSuppressedEvents(g, overrides);
    if (kept) out.push(kept);
  }
  return out;
}

/**
 * #1396 — flag + comment is a NON-destructive annotation, distinct from
 * both resolution (#1289) and suppression (#1395): a flagged document
 * stays fully in the canvas, the feed AND the totals — the flag only
 * surfaces a marker (editor) and a user-visible note (feed) capturing
 * *why* a reviewer singled the document out. The persisted source of
 * truth is the ``flagged_at`` timestamp on the event entity, written by
 * ``twinService.editEntity`` exactly like ``suppressed_at``; the override
 * semantics mirror ``isEventSuppressed`` so the editor's kebab flips the
 * flag badge instantly. There is no group-level exclusion helper for the
 * flag — by design it changes nothing about which groups render.
 */
export function isEventFlagged(
  ev: Record<string, unknown> | null | undefined,
  overrides?: ReadonlyMap<string, boolean>,
): boolean {
  if (!ev) return false;
  const entityId = String(ev.entity_id ?? '');
  if (entityId && overrides) {
    const override = overrides.get(entityId);
    if (override !== undefined) return override;
  }
  const raw = ev.flagged_at;
  return typeof raw === 'string' && raw.length > 0;
}

/**
 * #1396 — the reviewer's free-text note for a flagged document. Returns
 * the trimmed comment, or ``null`` when the event carries no usable note
 * (empty / whitespace-only / missing / non-string). A bare flag (no
 * comment) is legitimate, so this is independent of ``isEventFlagged``;
 * callers render the comment as a tooltip / sub-line only when present.
 */
export function eventFlagComment(
  ev: Record<string, unknown> | null | undefined,
): string | null {
  if (!ev) return null;
  const raw = ev.flag_comment;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function isOperatorResolved(
  g: MatchGroup,
  overrides?: ReadonlyMap<string, boolean>,
): boolean {
  const eventResolved = (ev: Record<string, unknown>) => isEventResolved(ev, overrides);
  // Codex round-1 P2: every arm requires at least one hydrated event —
  // ``[].every(...)`` is vacuously true, so an orphan whose event
  // failed to hydrate (partial entity fetch, tombstoned row) would
  // otherwise silently vanish from feed AND dashboard as
  // "operator-resolved". (The partial arm below already guarded this.)
  if (g.status === 'orphan_settlement') {
    const ses = seEventsOf(g);
    return ses.length > 0 && ses.every(eventResolved);
  }
  if (g.status === 'orphan_delivery') {
    const des = deEventsOf(g);
    return des.length > 0 && des.every(eventResolved);
  }
  if (g.status === 'partial') {
    const ses = seEventsOf(g);
    const des = deEventsOf(g);
    const allSeResolved = ses.length > 0 && ses.every(eventResolved);
    const allDeResolved = des.length > 0 && des.every(eventResolved);
    return allSeResolved && allDeResolved;
  }
  return false;
}
